import React, { useState, useEffect } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import PublicNavigation from '../../components/PublicNavigation';
import './PDFComparison.css';

function PDFComparison() {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [month1, setMonth1] = useState('');
  const [month2, setMonth2] = useState('');
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [differences, setDifferences] = useState([]);
  const [pdfLibLoaded, setPdfLibLoaded] = useState(false);

  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  useEffect(() => {
    // Load PDF.js library
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
    script.async = true;
    script.onload = () => {
      if (window['pdfjs-dist/build/pdf']) {
        window['pdfjs-dist/build/pdf'].GlobalWorkerOptions.workerSrc = 
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
        setPdfLibLoaded(true);
      }
    };
    script.onerror = () => {
      alert('Failed to load PDF library. Please refresh the page.');
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleFile1Change = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFile1(file);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleFile2Change = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setFile2(file);
    } else {
      alert('Please select a PDF file');
    }
  };

  const extractDataFromPDF = async (file) => {
    try {
      if (!window['pdfjs-dist/build/pdf']) {
        throw new Error('PDF library not loaded. Please refresh the page.');
      }

      const pdfjsLib = window['pdfjs-dist/build/pdf'];
      
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;
      
      let allPages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        allPages.push({
          pageNumber: i,
          text: pageText
        });
      }

      return parseReportDataWithPages(allPages);
    } catch (error) {
      console.error('PDF extraction error:', error);
      throw new Error(`Failed to read PDF: ${error.message}`);
    }
  };

  const parseReportDataWithPages = (pages) => {
    const projects = [];
    let currentChapter = 'Unknown Chapter';
    let currentSection = '';
    
    pages.forEach((page, pageIndex) => {
      const lines = page.text.split('\n');
      let currentProject = {};
      let lineNumber = 0;
      
      lines.forEach(line => {
        lineNumber++;
        line = line.trim();
        if (!line) return;

        // Detect chapter/section headers (customize based on your PDF format)
        if (line.match(/^chapter|^section|^part \d+/i)) {
          currentChapter = line;
        }

        // Detect district names
        const districts = ['Chennai', 'Coimbatore', 'Salem', 'Madurai', 'Trichy', 'Tirunelveli'];
        const foundDistrict = districts.find(d => line.toLowerCase().includes(d.toLowerCase()));
        if (foundDistrict) {
          currentSection = foundDistrict;
        }

        // Look for project indicators
        if (line.match(/project|school|beneficiar|district|category/i)) {
          // Extract key-value pairs
          if (line.includes(':')) {
            const [key, value] = line.split(':').map(s => s.trim());
            if (key && value) {
              currentProject[key.toLowerCase()] = value;
            }
          }
          
          // Look for numeric data
          const numbers = line.match(/\d+/g);
          if (numbers && numbers.length > 0) {
            if (line.match(/beneficiar/i)) {
              currentProject.beneficiaries = parseInt(numbers[0]);
            }
            if (line.match(/fund|amount|₹|rs/i)) {
              currentProject.funds = parseInt(numbers[0]);
            }
          }
        }

        // New project indicator (customize this pattern)
        if (line.match(/^project \d+|^[A-Z]{2,}|school name:/i)) {
          if (Object.keys(currentProject).length > 2) {
            projects.push({
              ...currentProject,
              pageNumber: page.pageNumber,
              lineNumber: lineNumber,
              chapter: currentChapter,
              section: currentSection
            });
          }
          currentProject = { name: line };
        }
      });

      // Add last project from this page
      if (Object.keys(currentProject).length > 2) {
        projects.push({
          ...currentProject,
          pageNumber: page.pageNumber,
          lineNumber: lineNumber,
          chapter: currentChapter,
          section: currentSection
        });
      }
    });

    return projects;
  };

  const compareProjects = (oldProjects, newProjects) => {
    const changes = [];

    // Create lookup maps
    const oldMap = new Map();
    oldProjects.forEach(p => {
      const key = `${p.name || ''}_${p.district || ''}_${p.school || ''}`.toLowerCase();
      oldMap.set(key, p);
    });

    const newMap = new Map();
    newProjects.forEach(p => {
      const key = `${p.name || ''}_${p.district || ''}_${p.school || ''}`.toLowerCase();
      newMap.set(key, p);
    });

    // Find new projects
    newMap.forEach((newProj, key) => {
      if (!oldMap.has(key)) {
        changes.push({
          type: 'NEW',
          project: key,
          change: 'New project added',
          oldValue: '-',
          newValue: JSON.stringify(newProj, null, 2),
          pageNumber: newProj.pageNumber,
          lineNumber: newProj.lineNumber,
          chapter: newProj.chapter,
          section: newProj.section,
          location: `Page ${newProj.pageNumber}, Line ${newProj.lineNumber}`,
          ...newProj
        });
      } else {
        // Compare existing projects
        const oldProj = oldMap.get(key);
        const diffs = findDifferences(oldProj, newProj);
        
        diffs.forEach(diff => {
          changes.push({
            type: 'MODIFIED',
            project: key,
            field: diff.field,
            change: diff.change,
            oldValue: diff.oldValue,
            newValue: diff.newValue,
            oldPage: oldProj.pageNumber,
            newPage: newProj.pageNumber,
            oldLine: oldProj.lineNumber,
            newLine: newProj.lineNumber,
            chapter: newProj.chapter,
            section: newProj.section,
            location: `Old: Page ${oldProj.pageNumber}, Line ${oldProj.lineNumber} → New: Page ${newProj.pageNumber}, Line ${newProj.lineNumber}`,
            ...newProj
          });
        });
      }
    });

    // Find removed projects
    oldMap.forEach((oldProj, key) => {
      if (!newMap.has(key)) {
        changes.push({
          type: 'REMOVED',
          project: key,
          change: 'Project removed or completed',
          oldValue: JSON.stringify(oldProj, null, 2),
          newValue: '-',
          pageNumber: oldProj.pageNumber,
          lineNumber: oldProj.lineNumber,
          chapter: oldProj.chapter,
          section: oldProj.section,
          location: `Was on Page ${oldProj.pageNumber}, Line ${oldProj.lineNumber}`,
          ...oldProj
        });
      }
    });

    return changes;
  };

  const findDifferences = (oldProj, newProj) => {
    const diffs = [];
    const allKeys = new Set([...Object.keys(oldProj), ...Object.keys(newProj)]);

    allKeys.forEach(key => {
      const oldVal = oldProj[key];
      const newVal = newProj[key];

      if (oldVal !== newVal) {
        diffs.push({
          field: key,
          change: `${key} changed`,
          oldValue: oldVal || 'N/A',
          newValue: newVal || 'N/A'
        });
      }
    });

    return diffs;
  };

  const handleCompare = async () => {
    if (!file1 || !file2) {
      alert('Please upload both PDF files');
      return;
    }

    if (!month1 || !month2) {
      alert('Please select months for both reports');
      return;
    }

    if (!pdfLibLoaded) {
      alert('PDF library is still loading. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    setResult(null);
    setDifferences([]);

    try {
      // Extract data from both PDFs
      const data1 = await extractDataFromPDF(file1);
      const data2 = await extractDataFromPDF(file2);

      console.log('Month 1 data:', data1);
      console.log('Month 2 data:', data2);

      // Compare and find differences
      const changes = compareProjects(data1, data2);

      setDifferences(changes);
      setResult({
        month1: month1,
        month2: month2,
        totalChanges: changes.length,
        newProjects: changes.filter(c => c.type === 'NEW').length,
        modified: changes.filter(c => c.type === 'MODIFIED').length,
        removed: changes.filter(c => c.type === 'REMOVED').length
      });

    } catch (error) {
      console.error('Error:', error);
      alert('Error processing PDFs: ' + error.message);
    }

    setProcessing(false);
  };

  const exportToCSV = () => {
    if (differences.length === 0) {
      alert('No differences to export');
      return;
    }

    // Create CSV header
    const headers = [
      'Type', 
      'Project/School', 
      'Chapter/Section',
      'Page & Line',
      'Field Changed', 
      'Description', 
      `${month1} Value`, 
      `${month2} Value`
    ];
    
    // Create CSV rows
    const rows = differences.map(diff => [
      diff.type,
      diff.project || diff.name || diff.school || 'Unknown',
      `${diff.chapter || 'N/A'} / ${diff.section || 'N/A'}`,
      diff.location || 'N/A',
      diff.field || '-',
      diff.change,
      diff.oldValue,
      diff.newValue
    ]);

    // Combine headers and rows
    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download CSV
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Report_Comparison_${month1}_vs_${month2}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="comparison-page">
      <PublicNavigation />
      
      <div className="comparison-container">
        <div className="comparison-header">
          <h1>📊 PDF Report Comparison Tool</h1>
          <p>Compare two monthly reports and export only the differences</p>
        </div>

        <div className="upload-section">
          <div className="upload-card">
            <div className="card-header">
              <h3>📄 First Report (Baseline)</h3>
              <span className="label">e.g., December 2025</span>
            </div>
            <div className="month-select">
              <label>Month:</label>
              <select value={month1} onChange={(e) => setMonth1(e.target.value)} required>
                <option value="">Select month</option>
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="file-upload">
              <input 
                type="file" 
                accept=".pdf" 
                onChange={handleFile1Change}
                id="file1"
              />
              <label htmlFor="file1" className="upload-btn">
                {file1 ? `✅ ${file1.name}` : '📎 Choose PDF File'}
              </label>
            </div>
          </div>

          <div className="vs-divider">VS</div>

          <div className="upload-card">
            <div className="card-header">
              <h3>📄 Second Report (New)</h3>
              <span className="label">e.g., January 2026</span>
            </div>
            <div className="month-select">
              <label>Month:</label>
              <select value={month2} onChange={(e) => setMonth2(e.target.value)} required>
                <option value="">Select month</option>
                {months.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="file-upload">
              <input 
                type="file" 
                accept=".pdf" 
                onChange={handleFile2Change}
                id="file2"
              />
              <label htmlFor="file2" className="upload-btn">
                {file2 ? `✅ ${file2.name}` : '📎 Choose PDF File'}
              </label>
            </div>
          </div>
        </div>

        <button 
          onClick={handleCompare} 
          className="compare-btn"
          disabled={!file1 || !file2 || !month1 || !month2 || processing}
        >
          {processing ? '⏳ Processing...' : '🔍 Compare Reports'}
        </button>

        {result && (
          <div className="results-section">
            <div className="results-header">
              <h2>Comparison Results: {month1} → {month2}</h2>
              <button onClick={exportToCSV} className="export-csv-btn">
                📥 Export Differences to CSV
              </button>
            </div>

            <div className="summary-cards">
              <div className="summary-card new">
                <div className="card-icon">➕</div>
                <div className="card-content">
                  <div className="card-value">{result.newProjects}</div>
                  <div className="card-label">New Projects/Schools</div>
                </div>
              </div>

              <div className="summary-card modified">
                <div className="card-icon">✏️</div>
                <div className="card-content">
                  <div className="card-value">{result.modified}</div>
                  <div className="card-label">Modified Fields</div>
                </div>
              </div>

              <div className="summary-card removed">
                <div className="card-icon">➖</div>
                <div className="card-content">
                  <div className="card-value">{result.removed}</div>
                  <div className="card-label">Removed/Completed</div>
                </div>
              </div>

              <div className="summary-card total">
                <div className="card-icon">📊</div>
                <div className="card-content">
                  <div className="card-value">{result.totalChanges}</div>
                  <div className="card-label">Total Changes</div>
                </div>
              </div>
            </div>

            <div className="differences-table">
              <h3>Detailed Differences</h3>
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Project/School</th>
                    <th>Chapter/Section</th>
                    <th>Location (Page, Line)</th>
                    <th>Field</th>
                    <th>Change Description</th>
                    <th>{month1} Value</th>
                    <th>{month2} Value</th>
                  </tr>
                </thead>
                <tbody>
                  {differences.map((diff, idx) => (
                    <tr key={idx} className={`diff-row ${diff.type.toLowerCase()}`}>
                      <td>
                        <span className={`type-badge ${diff.type.toLowerCase()}`}>
                          {diff.type}
                        </span>
                      </td>
                      <td>{diff.project || diff.name || diff.school || 'Unknown'}</td>
                      <td>
                        <div className="chapter-info">
                          <div><strong>{diff.chapter || 'N/A'}</strong></div>
                          <div className="section-tag">{diff.section || 'N/A'}</div>
                        </div>
                      </td>
                      <td className="location-cell">{diff.location || 'N/A'}</td>
                      <td>{diff.field || '-'}</td>
                      <td>{diff.change}</td>
                      <td>{diff.oldValue}</td>
                      <td>{diff.newValue}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="instructions">
          <h3>📋 How to Use:</h3>
          <ol>
            <li>Select the baseline month (e.g., December 2025)</li>
            <li>Upload the first PDF report</li>
            <li>Select the comparison month (e.g., January 2026)</li>
            <li>Upload the second PDF report</li>
            <li>Click "Compare Reports"</li>
            <li>Review differences and export to CSV</li>
          </ol>
          <p className="note">
            <strong>Note:</strong> The tool automatically detects new projects, modifications to existing data, 
            and removed/completed projects. Only differences are included in the CSV export.
          </p>
          {!pdfLibLoaded && (
            <p className="loading-note">
              ⏳ Loading PDF processing library...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default PDFComparison;
